import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import VStack from "src/sudokit/Layout/VStack";
import { APP_DOMAIN } from "../../../../../src/shared/constants";
import Auth from "../../../Auth";

export default function InvocationsSearch({}) {
  const [invocations, setInvocations] = useState([]);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [displayResults, setDisplayResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Function to perform the search
  const performSearch = (searchTerm) => {
    if (!searchTerm) return;

    setSearching(true);
    setHasSearched(true);

    Auth.fetchWithIdToken(`/api/admin/invocations/search`, {
      search: searchTerm,
    })
      .then((res) => {
        setInvocations(res.invocations);
        setSearching(false);
      })
      .catch((error) => {
        console.error("Search error:", error);
        setSearching(false);
      });
  };

  // Effect to read search query from URL on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get("q");

    if (queryParam) {
      setSearch(queryParam);
      performSearch(queryParam);
    }
  }, [location.search]); // Re-run when URL search params change

  // Use effect to properly handle the transition between states
  useEffect(() => {
    if (searching) {
      // When searching starts, immediately clear the display results
      setDisplayResults([]);
    } else {
      // When searching ends, update display results with the fetched invocations
      setDisplayResults(invocations);
    }
  }, [searching, invocations]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Update URL with search query
    history.replace({ search: search ? `?q=${encodeURIComponent(search)}` : "" });

    performSearch(search);
  };

  // Helper function to highlight search matches in text
  const highlightSearchMatches = (text, searchTerm) => {
    if (!text || !searchTerm) return text;

    // Create a regex with case insensitive flag
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");

    // Split the text by the regex
    const parts = text.split(regex);

    // Map the parts to JSX, highlighting matches
    return parts.map((part, i) => {
      // Check if this part matches the search term (case insensitive)
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <span
            key={i}
            className="bg-yellow-300 text-gray-900 font-medium"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };
  return (
    <VStack gap={2}>
      <Text className="text-base font-medium">Search Invocations</Text>

      <form
        className="flex mb-4 gap-2"
        onSubmit={handleSubmit}
      >
        <input
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-app-primary-cta focus:border-app-primary-cta placeholder-gray-500"
          type="text"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          placeholder="Search term..."
        />
        <SudoButton
          type="submit"
          variant="primary"
          size="default"
          state={searching ? "loading" : "default"}
          disabled={searching}
        >
          {searching ? "Searching..." : "Search"}
        </SudoButton>
      </form>
      {searching && (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600 flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-app-primary-cta"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <Text>Searching...</Text>
          </div>
        </div>
      )}

      {!searching && (
        <ul className="list-none p-0 space-y-3">
          {displayResults.length > 0 ? (
            displayResults.map((invocation) => {
              // Generate URLs
              const adminUrl = `https://${APP_DOMAIN}/admin/invocation/${invocation.invocationId}`;
              const langfuseUrl = invocation.langfuseTraceId ? `https://cloud.langfuse.com/project/clxf4brjw0003y8x6yd7f7apr/traces/${invocation.langfuseTraceId}` : `No Langfuse trace available`;

              return (
                <li
                  className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200"
                  key={`result-${invocation.invocationId}`}
                >
                  <VStack gap={2}>
                    <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
                      <Text>{invocation.source}</Text>
                      <Text>&middot;</Text>
                      <Text>{new Date(invocation.createdAt.value).toLocaleString()}</Text>
                      <Text>&middot;</Text>
                      <a
                        href={`/admin/user/${invocation.email}`}
                        className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline"
                      >
                        {invocation.email}
                      </a>
                    </div>

                    {invocation.snippet && (
                      <div className="text-gray-700 text-sm bg-white p-3 rounded border-l-4 border-app-primary-cta whitespace-pre-wrap break-words leading-tight">
                        {highlightSearchMatches(invocation.snippet, search)}
                      </div>
                    )}

                    <div className="text-xs items-center flex flex-wrap gap-x-2">
                      <a
                        className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline font-mono"
                        href={adminUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {invocation.invocationId}
                      </a>
                      <Text className="text-gray-400">|</Text>
                      {invocation.langfuseTraceId ? (
                        <a
                          className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline"
                          href={langfuseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Langfuse ↗
                        </a>
                      ) : (
                        <Text className="text-gray-500 italic">No Langfuse trace</Text>
                      )}
                    </div>
                  </VStack>
                </li>
              );
            })
          ) : hasSearched ? (
            <li className="text-gray-500 text-center py-8">
              <Text>No results found</Text>
            </li>
          ) : null}
        </ul>
      )}
    </VStack>
  );
}
